import React from 'react';
import { Card, Typography, Space, Row, Col, Divider } from 'antd';
import { useTranslation } from 'react-i18next';

const { Paragraph } = Typography;

const RubbishInstructions = () => {
  const { t } = useTranslation();
  
  const wasteTypes = [
    {
      icon: '🗑️',
      title: t('rubbish.types.general.title'),
      description: t('rubbish.types.general.description'),
      color: '#52c41a',
      bgColor: '#f6ffed',
      borderColor: '#b7eb8f'
    },
    {
      icon: '♻️',
      title: t('rubbish.types.plastic.title'),
      description: t('rubbish.types.plastic.description'),
      color: '#1890ff',
      bgColor: '#e6f7ff',
      borderColor: '#91d5ff'
    },
    {
      icon: '📄',
      title: t('rubbish.types.paper.title'),
      description: t('rubbish.types.paper.description'),
      color: '#faad14',
      bgColor: '#fffbe6',
      borderColor: '#ffe58f'
    },
    {
      icon: '🍾',
      title: t('rubbish.types.glass.title'),
      description: t('rubbish.types.glass.description'),
      color: '#722ed1',
      bgColor: '#f9f0ff',
      borderColor: '#d3adf7'
    }
  ];
  
  return (
    <Card 
      title={
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>🗑️</div>
          <div>{t('rubbish.title').replace('🗑️ ', '')}</div>
        </div>
      } 
      style={{ marginBottom: '24px' }}
      headStyle={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: '8px 8px 0 0'
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Introduction */}
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f0f9ff', 
          borderRadius: '12px',
          border: '1px solid #bae6fd'
        }}>
          <Paragraph style={{ 
            fontSize: '16px', 
            margin: 0,
            color: '#0369a1',
            textAlign: 'center',
            lineHeight: '1.6'
          }}>
            {t('rubbish.intro')}
          </Paragraph>
        </div>
        
        <Divider orientation="left" style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
          📍 {t('rubbish.whereToPlace.title')}
        </Divider>
        
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#fef3c7', 
          borderRadius: '8px',
          border: '1px solid #fcd34d',
          borderLeft: '4px solid #f59e0b'
        }}>
          <Paragraph style={{ margin: 0, color: '#92400e', fontSize: '15px' }}>
            {t('rubbish.whereToPlace.description')}
          </Paragraph>
        </div>
        
        <Divider orientation="left" style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
          📅 {t('rubbish.scheduleTitle')}
        </Divider>
        
        {/* Collection Schedule Cards */}
        <Row gutter={[16, 16]}>
          {wasteTypes.map((waste, index) => (
            <Col xs={24} sm={12} key={index}>
              <Card
                size="small"
                style={{ 
                  height: '100%',
                  backgroundColor: waste.bgColor,
                  border: `1px solid ${waste.borderColor}`,
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}
                bodyStyle={{ padding: '16px' }}
                hoverable
              >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '8px' 
                  }}>
                    <span style={{ fontSize: '24px', marginRight: '12px' }}>
                      {waste.icon}
                    </span>
                    <Typography.Title level={5} style={{ 
                      margin: 0, 
                      color: waste.color,
                      fontWeight: 'bold'
                    }}>
                      {waste.title}
                    </Typography.Title>
                  </div>
                  <Paragraph style={{ 
                    margin: 0, 
                    color: '#374151',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {waste.description}
                  </Paragraph>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
        
        <Divider orientation="left" style={{ fontSize: '18px', fontWeight: 'bold', color: '#1f2937' }}>
          💡 {t('rubbish.additionalNotesTitle')}
        </Divider>
        
        {/* Additional Notes */}
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {[
              { icon: '🔄', text: t('rubbish.additionalNotes.separation') },
              { icon: '🧽', text: t('rubbish.additionalNotes.rinse') },
              { icon: '⏰', text: t('rubbish.additionalNotes.timing') }
            ].map((note, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <span style={{ 
                  fontSize: '20px', 
                  marginRight: '12px',
                  marginTop: '2px'
                }}>
                  {note.icon}
                </span>
                <Paragraph style={{ 
                  margin: 0, 
                  color: '#374151',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {note.text}
                </Paragraph>
              </div>
            ))}
          </Space>
        </div>
        
        {/* Contact Section */}
        <div style={{ 
          marginTop: '24px', 
          padding: '20px', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>📞</div>
          <Paragraph style={{ 
            margin: 0, 
            color: 'white',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            {t('rubbish.contact')}
          </Paragraph>
        </div>
      </Space>
    </Card>
  );
};

export { RubbishInstructions };
